import React, { useEffect, useRef, useState } from "react";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { format, isValid, parse } from "date-fns";

const DISPLAY = "MM/dd/yyyy";

// mm/dd/yyyy as shown in the form (2c, Figma); the request still sends YYYY-MM-DD.
export const formatDisplayDate = (date) => (date instanceof Date && isValid(date) ? format(date, DISPLAY) : "");

// Strict: "02/30/2026", "13/45/2026" and partial input are invalid (an Invalid Date, not null).
export const parseDisplayDate = (text) => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const date = parse(trimmed, DISPLAY, new Date());
  return isValid(date) && format(date, DISPLAY) === trimmed ? date : new Date(NaN);
};

const sameDay = (a, b) =>
  (a == null && b == null) || (a instanceof Date && b instanceof Date && (a.getTime() === b.getTime() || (isNaN(a) && isNaN(b))));

// A typed mm/dd/yyyy field with a calendar button that opens the dialog-style calendar.
// The calendar's choice is applied only on OK; Cancel keeps the field as it was.
const DateField = ({ id, label, value, onChange, required, error, helperText }) => {
  const [text, setText] = useState(formatDisplayDate(value));
  const [open, setOpen] = useState(false);
  // The calendar's own value while it is open: a day click commits to it (MUI X v5 needs the
  // controlled value to follow onChange); only OK copies it into the field.
  const [pending, setPending] = useState(null);
  const emitted = useRef(value);

  // Follow changes that didn't come from typing here (calendar, Edit pre-fill, clearing the end date).
  useEffect(() => {
    if (!sameDay(value, emitted.current)) {
      emitted.current = value;
      setText(formatDisplayDate(value));
    }
  }, [value]);

  const emit = (date) => {
    emitted.current = date;
    onChange(date);
  };

  const invalid = text.trim() !== "" && !(value instanceof Date && isValid(value));
  const openCalendar = () => {
    setPending(value instanceof Date && isValid(value) ? value : null);
    setOpen(true);
  };

  return (
    <>
      <TextField
        id={id}
        label={label}
        required={required}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          emit(parseDisplayDate(e.target.value));
        }}
        placeholder="mm/dd/yyyy"
        InputLabelProps={{ shrink: true }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton edge="start" size="small" aria-label={`Choose ${label}`} onClick={openCalendar} sx={{ color: "#483C3B" }}>
                <CalendarMonthOutlinedIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        error={Boolean(error) || invalid}
        helperText={error || (invalid ? "Enter a date as mm/dd/yyyy" : helperText)}
        fullWidth
      />
      <MobileDatePicker
        open={open}
        onOpen={openCalendar}
        onClose={() => setOpen(false)}
        value={pending}
        onChange={setPending}
        onAccept={(date) => {
          if (date) {
            setText(formatDisplayDate(date));
            emit(date);
          }
        }}
        renderInput={(params) => <TextField {...params} sx={{ display: "none" }} inputProps={{ ...params.inputProps, tabIndex: -1, "aria-hidden": true }} />}
      />
    </>
  );
};

export default DateField;
